import GPCircleThumb from '@/components/GPCircleThumb';
import GPLink from '@/components/GPLink';
import { useMemberThumbs } from '@/requests/membership';
import { EventListItem as EventListItemType } from '@/types/event';
import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { DollarCircle, NoteText, Star1, Teacher, TickSquare } from 'givepulse-iconsax-react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { statusEventCardIcon } from '../GPSelector';
import ListItemTagBar from './Widgets/ListItemTagBar';
import { useWhitelabelTranslation } from '@/contexts/WhitelabelTranslationContext';

const useStyles = makeStyles()(theme => ({
  root: {
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      height: 'auto',
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
  },

  imageBackground: {
    background: theme.palette.background.gradient,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
  },

  kindOfEventContainer: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '999em',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      top: 28,
      right: 10,
    },
  },

  rightContainer: {
    display: 'flex',
    flexGrow: 1,
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexGrow: 0,
    },
  },

  rightContainerMap: {
    flexShrink: 0,
    position: 'relative',
    height: '100%',
    width: '56.5%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexGrow: 0,
    },
  },

  memberAvatar: {
    width: 25,
    height: 25,
  },

  thumbContainer: {
    display: 'flex',
    marginTop: theme.spacing(1),
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },

  iconContainer: {
    display: 'inline',
    alignItems: 'center',
    alignSelf: 'flex-start',
    minWidth: 100,
    flexShrink: 0,
    textAlign: 'left',
    color: theme.palette.grey[500],
    paddingRight: theme.spacing(3),
    '& > *': {
      marginBottom: theme.spacing(0.5),
    },
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
    [theme.breakpoints.down('md')]: {
      minWidth: 0,
    },
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
    },
  },

  imageContainer: {
    height: '100%',
    minHeight: 152,
    width: 330, // (200 - 48) * 1.75
    marginRight: theme.spacing(3),
    flexShrink: 0,
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 5,
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      paddingTop: '46.7%',
      width: '100%',
      marginRight: 0,
      marginBottom: theme.spacing(1),
    },
  },

  internshipIcons: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 10,
    flexDirection: 'row',
    flexWrap: 'wrap',
    color: 'rgba(0, 0, 0, 0.6)',
  },

  icon: {
    marginRight: 3,
    fontSize: 20,
  },

  internshipIconsWrapped: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },

  lineEllipses: {
    // width: '50%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'wrap',
  },
}));

type EventPublicListProps = EventListItemType & {
  headDivider?: boolean;
  tailDivider?: boolean;
  isMapOnSide?: boolean;
  showIcons?: boolean;
  headerTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
};

export const EventListItemSkeleton = () => {
  const { classes } = useStyles();

  return (
    <>
      <Stack
        direction="row"
        className={classes.root}
        sx={{
          gap: 2,
          width: 1
        }}>
        <Skeleton variant="rectangular" sx={{ flex: '0 0 30%' }} height="auto" />

        <Stack sx={{
          gap: 1
        }}>
          <Skeleton variant="text" width="20ch" />
          <Skeleton variant="text" width="16ch" />
          <Skeleton variant="text" width="12ch" />

          <Box sx={{
            display: "flex"
          }}>
            {[...Array.from(Array(3).keys())].map(id => (
              <Box key={id} sx={{
                mr: 1
              }}>
                <Skeleton variant="circular" width={30} height={30} />
              </Box>
            ))}
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

const EventPublicListItem = ({
  showIcons = true,
  headerTag = 'h3',
  ...event
}: EventPublicListProps) => {
  const { pathname } = useRouter();

  const isSearchPage = pathname === '/search/[[...location]]';

  const { data: memberData, isValidating: loadingMember } = useMemberThumbs({
    objectId: `${event.event_id ?? null}`,
    objectType: 'event',
    pageSize: 6,
    skip: !event.display_participants,
    role: event && (event.internship || event.kind_of_event_raw === 'resource') ? 1024 : null,
    filters: { orderBy: 'nice' },
  });

  const totalMembers = memberData?.meta.total ?? 0;
  const members = memberData?.members ?? null;

  const { classes } = useStyles();
  const { t: tGroupView } = useTranslation('groupView');
  const { t: tEventView } = useTranslation('eventView');
  const { t: tWhitelabel } = useWhitelabelTranslation();

  const addressText = useMemo(() => {
    if (event.virtual) {
      return tGroupView('Virtual/Remote');
    }

    if (event.location?.address?.city && event.location.address.state) {
      return `${event.location.address.city}, ${event.location.address.state}`;
    }

    return event.location?.address?.city || event.location?.address?.state || '';
  }, [event.virtual, event.location?.address?.city, event.location?.address?.state, tGroupView]);

  return (
    <GPLink href={event.path} color="inherit" underline="none">
      <Stack
        direction="row"
        className={classes.root}
        sx={{
          gap: 2,
          paddingY: 1
        }}>
        {/* Left Section - image */}
        <Box
          className={classes.imageBackground}
          sx={{
            position: "relative",
            flex: "0 0 30%",
            border: 1,
            borderColor: "transparent",
            borderRadius: 2,
            overflow: "hidden",
            height: '125px'
          }}>
          {event.cover?.url && (
            <Image
              src={event.cover.url || ''}
              alt={event.cover?.alt_text || `Cover image for ${event.title || 'Event'}`}
              fill
              style={{ objectFit: 'cover' }}
            />
          )}
        </Box>
        {/* Right Section - body */}
        <Stack
          sx={{
            gap: 0.5,
            flex: "1 1 auto"
          }}>
          {!Boolean(Number(event.hide_positions)) && event.pos_left !== null && (
            <Chip
              label={event.pos_left === 0 ? 'Full' : event.pos_left}
              size="small"
              sx={{
                backgroundColor: '#FAEBBE',
                color: event.pos_left === 0 ? '#DC374C' : '#222222',
                fontWeight: 500,
                fontSize: '12px',
                height: 24,
                alignSelf: 'flex-start',
                '& .MuiChip-label': {
                  padding: '0 8px',
                },
              }}
            />
          )}
          <Typography component={headerTag} variant="h5">
            {event.title}
          </Typography>
          {!!event.datetime_text && <Typography>{event.datetime_text}</Typography>}
          {/* Type of Event */}
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 0.5
            }}>
            {statusEventCardIcon(event.kind_of_event_raw)}
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {event.kind_of_event_raw === 'internship'
                ? tWhitelabel('Internship')
                : event.kind_of_event}
            </Typography>
          </Stack>

          {event.internship && (
            <Box className={classes.internshipIconsWrapped}>
              {event.internship && !!event.internship.paid && (
                <span className={classes.internshipIcons}>
                  <DollarCircle className={classes.icon} size="22" />
                  <Typography variant="caption">
                    {event.internship.paid === 2
                      ? tEventView('Work-study Eligible')
                      : tGroupView('Paid')}
                  </Typography>
                </span>
              )}

              {event.internship && !!event.internship.credit && (
                <span className={classes.internshipIcons}>
                  <Teacher size="22" className={classes.icon} />
                  <Typography variant="caption">{tGroupView('Credit')}</Typography>
                </span>
              )}

              {event.internship && event.internship.timeframe && (
                <span className={classes.internshipIcons}>
                  <NoteText className={classes.icon} size="22" />
                  <Typography variant="caption">
                    {event.internship.timeframe && event.internship.timeframe.join(', ')}
                  </Typography>
                </span>
              )}

              {event.internship.requirements &&
                event.internship.requirements.includes('backgroundcheck') && (
                  <span className={classes.internshipIcons}>
                    <TickSquare className={classes.icon} size="22" />
                    <Typography variant="caption">{tGroupView('Background Check')}</Typography>
                  </span>
                )}
            </Box>
          )}
          <Box sx={{
            width: 1
          }}>
            {(!!addressText || event.group) && (
              <Typography className={classes.lineEllipses} color="textSecondary">
                {event.group && (
                  // <GPLink underline="hover" color="inherit" href={event.group.path}>
                  (<>
                    {tGroupView('Listed by')} {event.group.title}
                  </>)
                  // </GPLink>
                )}
                {!!addressText && (
                  <>
                    {event.group && ' · '}
                    {addressText}
                  </>
                )}
              </Typography>
            )}
          </Box>
          {!loadingMember ? (
            event.display_participants &&
            members &&
            members.length > 0 && (
              <Box className={classes.thumbContainer}>
                {members?.slice(0, 6).map(member => (
                  <Box
                    key={member.unique_id}
                    sx={{
                      width: 24,
                      height: 24
                    }}>
                    <GPCircleThumb
                      key={member.unique_id}
                      avatar
                      size={24}
                      tooltip={member.name}
                      thumbUrl={member.thumb_url}
                      thumbName={member.name}
                    />
                  </Box>
                ))}
                {totalMembers > 6 && (
                  <Typography variant="caption">
                    {tGroupView('+X more', { count: totalMembers - 6 })}
                  </Typography>
                )}
              </Box>
            )
          ) : (
            <Box className={classes.thumbContainer}>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="circular" width={24} height={24} />
            </Box>
          )}
          <ListItemTagBar
            tags={[...(event.tags ?? []), ...(event.causes ?? [])]}
            rootSx={{
              // translateY helps bypass margin | padding related layout shifts
              transform: theme => `translateY(${theme.spacing(0.5)})`,
            }}
          />
        </Stack>
        {showIcons && (
          <Box className={classes.iconContainer}>
            {event.promoted && !isSearchPage && <Star1 size="22" />}
          </Box>
        )}
      </Stack>
    </GPLink>
  );
};

export default EventPublicListItem;
